import { useMemo, useCallback, useState, useRef } from "react"
import PropTypes from "prop-types"
import { sortBy } from "lodash"
import SearchableMenu from "components/searchable-menu"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "connected-components/dashboard/analytics"
import { useAddModule } from "connected-components/dashboard/hooks/use-add-module"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { useAccountAlias, useIsDashboardCustomGraphFeatureEnabled } from "resources/account"
import { useTemplates } from "resources/templates"
import { useModelMetadata } from "resources/models"
import {
  buildSecondaryModuleConfigFromMenuItem,
  SECONDARY_MODULE_CONFIG_VIEW_KEYS,
} from "connected-components/dashboard/helpers/module-renderers"
import {
  getOpenInNewTabMergeOptions,
  mergeItemsWithOrderAndSecondaryAction,
} from "connected-components/dashboard/components/view-selectors/assets-menu-items"
import { buildCustomTemplateMenuActions } from "connected-components/dashboard/components/view-selectors/build-custom-template-menu-actions"
import { resetDuplicateFlowOnViewSelect } from "connected-components/asset-view/helpers/duplicate-view-flow"
import { useContentSetsMetadata } from "resources/content-sets"
import { OK } from "helpers/data-statuses"
import { DEFAULT_GRAPH_STATE } from "connected-components/dashboard/store/graph-slice"
import { GRAPH_CUSTOM_TEMPLATE_TYPE, CONTENT_SET_TEMPLATE_PREFIX, GRAPH_CATEGORY_DEFAULTS, gateCustomTemplatesResult } from "../templates/template-chart-config"
import { sortCustomTemplatesByFamily, getCustomGraphFamilyGroupId, getCustomGraphFamilyGroupLabel, isAddedMetricsVisibleForView, getResolvableAddedMetrics } from "../templates/custom/family"
import CustomTemplateFamilyProbes from "./custom-template-family-probes"
import { useGraphData } from "../hooks/use-graph-data"
import { useGraphDuplicateFlow } from "../hooks/use-graph-duplicate-flow"
import { useGraphTemplateChart } from "../hooks/use-graph-template-chart"

export { CONTENT_SET_TEMPLATE_PREFIX }

const UNRESOLVED_FAMILY_GROUP_ID = "custom-unresolved"
const UNRESOLVED_FAMILY_GROUP_LABEL = "Custom"

const BASE_MENU_ITEMS = [
  {
    id: "performance",
    label: "Performance",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "risk",
    label: "Risk",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "exposure",
    label: "Exposure",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "composition",
    label: "Composition",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "beta",
    label: "Beta",
    groupId: "model",
    groupLabel: "Risk model",
  },
  {
    id: "factor-sensitivity-predicted",
    label: "Predicted Factor Sensitivity",
    groupId: "factor-sensitivity",
    groupLabel: "Factor Sensitivity",
  },
  {
    id: "factor-sensitivity-historical",
    label: "Historical Factor Sensitivity",
    groupId: "factor-sensitivity",
    groupLabel: "Factor Sensitivity",
  },
]

export const GRAPH_TEMPLATE_LABELS = Object.fromEntries(BASE_MENU_ITEMS.map(item => [item.id, item.label]))

function buildContentSetMenuItems(contentSetsMetadata, contentSetsStatus) {
  if (contentSetsStatus !== OK || !contentSetsMetadata?.length) return []
  return contentSetsMetadata.map(contentSet => ({
    id: `${CONTENT_SET_TEMPLATE_PREFIX}${contentSet.id}`,
    label: contentSet.name,
    groupId: contentSet.proprietary ? "proprietary" : "licensed",
    groupLabel: contentSet.proprietary ? "Proprietary" : "Licensed",
  }))
}

// Gated the same way as GraphWrapper's Reset/Save-as-view actions, not just addedMetrics.length > 0 — keeps the chip from outliving a hop to Custom or a stale id.
function withModifiedSuffix(label, isModified) {
  if (!isModified) return label
  return <>{label} · modified</>
}

export default function GraphTemplateSelector({ disablePortal = false }) {
  const { selectedTemplate, setSelectedTemplate, setShowMetricsSelector, clearDuplicateState, deleteTemplate, dateOverride, getCurrentViewId, addedMetrics, modelId } = useGraphData()
  const { selectedCategory } = useGraphTemplateChart(selectedTemplate, GRAPH_CATEGORY_DEFAULTS)
  const [contentSetsMetadata, contentSetsStatus] = useContentSetsMetadata()
  // Reads through the shared resource cache rather than re-fetching.
  const [modelMetadata] = useModelMetadata(modelId)
  const activeContentSet = useMemo(
    () => contentSetsMetadata?.find((contentSetItem) => `${CONTENT_SET_TEMPLATE_PREFIX}${contentSetItem.id}` === selectedTemplate),
    [contentSetsMetadata, selectedTemplate],
  )
  const resolvableAddedMetrics = useMemo(
    () => getResolvableAddedMetrics(addedMetrics, selectedTemplate, modelMetadata, activeContentSet),
    [addedMetrics, selectedTemplate, modelMetadata, activeContentSet],
  )
  const isModified = resolvableAddedMetrics.length > 0 && isAddedMetricsVisibleForView(selectedTemplate, selectedCategory)
  const getLabel = useCallback((label) => withModifiedSuffix(label, isModified), [isModified])
  const isCustomGraphFeatureEnabled = useIsDashboardCustomGraphFeatureEnabled()
  const [rawCustomTemplates] = useTemplates(GRAPH_CUSTOM_TEMPLATE_TYPE)
  const customTemplates = gateCustomTemplatesResult(rawCustomTemplates, isCustomGraphFeatureEnabled)
  const analytics = useAnalytics()
  const addModule = useAddModule()
  const account = useAccountAlias()
  const duplicateFlow = useGraphDuplicateFlow()

  const [hasMenuEverOpened, setHasMenuEverOpened] = useState(false)
  const handleMenuOpenChange = useCallback((isOpen) => {
    if (isOpen) setHasMenuEverOpened(true)
  }, [])

  const [customTemplateFamilies, setCustomTemplateFamilies] = useState({})
  const handleFamiliesResolved = useCallback((resolutions) => {
    setCustomTemplateFamilies(prev => {
      const entriesChanged = Object.entries(resolutions).some(
        ([templateId, context]) => prev[templateId]?.family !== context?.family,
      )
      return entriesChanged ? { ...prev, ...resolutions } : prev
    })
  }, [])
  const customTemplateIds = useMemo(() => (customTemplates ?? []).map(template => template.id), [customTemplates])

  const deletePendingByTemplateIdRef = useRef({})
  const withDeletePendingGuard = useCallback((templateId, action) => {
    if (action.id !== "delete") {
      return action
    }
    return {
      ...action,
      onClick: async () => {
        if (deletePendingByTemplateIdRef.current[templateId]) return
        deletePendingByTemplateIdRef.current[templateId] = true
        try {
          await action.onClick()
        } finally {
          deletePendingByTemplateIdRef.current[templateId] = false
        }
      },
    }
  }, [])

  const handleOpenInNewTab = useCallback((item, event) => {
    analytics.track(DASHBOARD_EVENTS.GRAPH_OPENED_AS_TAB, { templateId: item.id })
    const config = buildSecondaryModuleConfigFromMenuItem(
      item,
      event,
      SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_VIEW,
    )
    addModule({ type: MODULE_TYPES.GRAPH, config: { ...config, selectedTemplate: item.id, dateOverride } })
  }, [analytics, addModule, dateOverride])

  const customMenuItems = useMemo(() => {
    const orderedTemplates = sortCustomTemplatesByFamily(sortBy(customTemplates, "name"), customTemplateFamilies)

    return orderedTemplates.map((template) => {
      const actions = buildCustomTemplateMenuActions(
        account,
        template,
        duplicateFlow,
        deleteTemplate,
        analytics.track,
        DEFAULT_GRAPH_STATE.selectedTemplate,
        getCurrentViewId,
      ).map((action) => withDeletePendingGuard(template.id, action))
      const family = customTemplateFamilies[template.id]?.family
      return {
        id: template.id,
        label: template.name,
        groupId: family ? getCustomGraphFamilyGroupId(family) : UNRESOLVED_FAMILY_GROUP_ID,
        groupLabel: family ? getCustomGraphFamilyGroupLabel(family) : UNRESOLVED_FAMILY_GROUP_LABEL,
        ...(actions.length > 0 && { actions }),
      }
    })
  }, [customTemplates, customTemplateFamilies, account, duplicateFlow, deleteTemplate, analytics, getCurrentViewId, withDeletePendingGuard])

  const rawMenuItems = useMemo(() => (
    [...customMenuItems, ...BASE_MENU_ITEMS, ...buildContentSetMenuItems(contentSetsMetadata, contentSetsStatus)]
  ), [customMenuItems, contentSetsMetadata, contentSetsStatus])

  const menuItems = useMemo(() => (
    mergeItemsWithOrderAndSecondaryAction([rawMenuItems], getOpenInNewTabMergeOptions(handleOpenInNewTab))
  ), [rawMenuItems, handleOpenInNewTab])

  const handleTemplateSelect = useCallback((selectedItem) => {
    analytics.track(DASHBOARD_EVENTS.GRAPH_TEMPLATE_SELECTED, {
      templateId: selectedItem.id,
      templateName: selectedItem.label,
    })
    resetDuplicateFlowOnViewSelect({ clearDuplicateState, setShowMetricsSelector })
    setSelectedTemplate(selectedItem.id)
  }, [analytics, setSelectedTemplate, clearDuplicateState, setShowMetricsSelector])

  return <div className="graph-template-selector">
    {isCustomGraphFeatureEnabled && hasMenuEverOpened && (
      <CustomTemplateFamilyProbes templateIds={customTemplateIds} onResolved={handleFamiliesResolved} />
    )}
    <SearchableMenu
      items={menuItems}
      current={selectedTemplate}
      onSelect={handleTemplateSelect}
      onOpenChange={handleMenuOpenChange}
      disablePortal={disablePortal}
      getLabel={getLabel}
    />
  </div>
}

GraphTemplateSelector.propTypes = {
  disablePortal: PropTypes.bool,
}
